import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from '@angular/common';
import { BackendApiService } from "../../../../app/services/backend-api.service";
import { TrendsChartComponent } from "../trends-chart/trends-chart.component";
import { BaseUrlConstantsService } from "../../../../app/services/base-url-constants.service";
interface ChartDataPoint {
  x: Date;
  y: number;
}

interface ChartConfig {
  loading: boolean;
  data: any;
  dataSeries: any[];
  thresholds: { green?: number; yellow?: number; red?: number };
}

@Component({
  selector: 'app-piezo-predictive',
  templateUrl: './predictive.component.html',
  styleUrl: './predictive.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    TrendsChartComponent
  ]
})
export class PiezoPredictive implements OnInit {
  @Input() data: any;
  public clientId: string | null = null;

  public charts: {
    vibrationAcceleration: ChartConfig;
    vibrationVelocity: ChartConfig;
    deltaUltrasound: ChartConfig;
    temperature: ChartConfig;
  };

  constructor(private service: BackendApiService) {
    this.clientId = localStorage.getItem('user_id');

    // Initialize all charts
    this.charts = {
      vibrationAcceleration: {
        loading: false,
        data: null,
        dataSeries: [],
        thresholds: {}
      },
      vibrationVelocity: {
        loading: false,
        data: null,
        dataSeries: [],
        thresholds: {}
      },
      deltaUltrasound: {
        loading: false,
        data: null,
        dataSeries: [],
        thresholds: {}
      },
      temperature: {
        loading: false,
        data: null,
        dataSeries: [],
        thresholds: {}
      }
    };
  }

  ngOnInit(): void {
    this.fetchVibrationAcceleration();
    // Uncomment when APIs are ready
    this.fetchVibrationVelocity();
    // this.fetchDeltaUltrasound();
    this.fetchTemperature();
    console.log(this.data)
  }

  async fetchVibrationAcceleration() {
    try {
      this.charts.vibrationAcceleration.loading = true;
      // const apiUrl = `https://em-in-api.enertics.ca/middleware/sensor/detail/${this.data.inventoryId}}/${this.data.assetId}/6,7,8/hour?type=1&userid=491&asset_type=GEAR`;
      const apiUrl = `${BaseUrlConstantsService.PIEZO_SENSOR_DETAIL}/${this.data.inventoryId}/${this.data.assetId}/43,44,45/hour?type=1&userid=${this.clientId}&asset_type=${this.data.assetType}`;
      const response: any = await this.service.getDataV2(apiUrl);

      if (response?.data?.body) {
        this.charts.vibrationAcceleration.data = response.data.body;
        this.charts.vibrationAcceleration.dataSeries = this.createDataSeries(response.data.body);
        this.charts.vibrationAcceleration.thresholds = {
          green: response.data.body.green,
          yellow: response.data.body.yellow,
          red: response.data.body.red
        };
      }
    } catch (error) {
      console.log('Error fetching vibration acceleration:', error);
    } finally {
      this.charts.vibrationAcceleration.loading = false;
    }
  }

  async fetchVibrationVelocity() {
    try {
      this.charts.vibrationVelocity.loading = true;
      // const apiUrl = `https://em-in-api.enertics.ca/middleware/sensor/velocity/2162/4011/49,50,51/hour?type=1&userid=491&asset_type=GEAR`;
      const apiUrl = `${BaseUrlConstantsService.PIEZO_SENSOR_VELOCITY}/${this.data.inventoryId}/${this.data.assetId}/49,50,51/hour?type=1&userid=${this.clientId}&asset_type=${this.data.assetType}`;
      const response: any = await this.service.getDataV2(apiUrl);

      if (response?.data?.body) {
        this.charts.vibrationVelocity.data = response.data.body;
        this.charts.vibrationVelocity.dataSeries = this.createDataSeries(response.data.body);
        this.charts.vibrationVelocity.thresholds = {
          green: response.data.body.green,
          yellow: response.data.body.yellow,
          red: response.data.body.red
        };
      }
    } catch (error) {
      console.log('Error fetching vibration velocity:', error);
    } finally {
      this.charts.vibrationVelocity.loading = false;
    }
  }

  async fetchTemperature() {
    try {
      this.charts.temperature.loading = true;
      // const apiUrl = `https://em-in-api.enertics.ca/middleware/sensor/detail/2162/4011/1,2,3/hour?type=1&userid=491&asset_type=GEAR`;
      const apiUrl = `${BaseUrlConstantsService.PIEZO_SENSOR_DETAIL}/${this.data.inventoryId}/${this.data.assetId}/46,47,48/hour?type=1&userid=${this.clientId}&asset_type=${this.data.assetType}`;
      const response: any = await this.service.getDataV2(apiUrl);

      if (response?.data?.body) {
        this.charts.temperature.data = response.data.body;
        this.charts.temperature.dataSeries = this.createDataSeries(response.data.body);
        this.charts.temperature.thresholds = {
          green: response.data.body.green,
          yellow: response.data.body.yellow,
          red: response.data.body.red
        };
      }
    } catch (error) {
      console.log('Error fetching temperature:', error);
    } finally {
      this.charts.temperature.loading = false;
    }
  }

  private parseDataPoints(data: any[]): ChartDataPoint[] {
    if (!data || !Array.isArray(data)) return [];

    return data.map(point => ({
      x: new Date(point.x),
      y: point.y
    }));
  }

  createDataSeries(chartData: any): any[] {
    const {
      chartXdata,
      chartYdata,
      chartZdata,
      nChartArrX,
      nChartArrY,
      nChartArrZ,
      labelx,
      labely,
      labelz
    } = chartData;

    const dataSeries: any[] = [];

    // Add solid line series for actual data
    if (chartXdata?.length) {
      dataSeries.push({
        type: "line",
        name: labelx || "Axial",
        showInLegend: true,
        xValueFormatString: "DD MMM YYYY HH:mm",
        yValueFormatString: "#0.#####",
        dataPoints: this.parseDataPoints(chartXdata),
        color: "#008FFB",
        lineThickness: 2,
        markerSize: 5,
        markerType: "circle"
      });
    }

    if (chartYdata?.length) {
      dataSeries.push({
        type: "line",
        name: labely || "Tangential (Vertical)",
        showInLegend: true,
        xValueFormatString: "DD MMM YYYY HH:mm",
        yValueFormatString: "#0.#####",
        dataPoints: this.parseDataPoints(chartYdata),
        color: "#00E396",
        lineThickness: 2,
        markerSize: 5,
        markerType: "circle"
      });
    }

    if (chartZdata?.length) {
      dataSeries.push({
        type: "line",
        name: labelz || "Radial (Horizontal)",
        showInLegend: true,
        xValueFormatString: "DD MMM YYYY HH:mm",
        yValueFormatString: "#0.#####",
        dataPoints: this.parseDataPoints(chartZdata),
        color: "#FEB019",
        lineThickness: 2,
        markerSize: 5,
        markerType: "circle"
      });
    }

    // Add dotted/dashed line series for predicted data (nChartArr)
    if (nChartArrX?.length) {
      dataSeries.push({
        type: "line",
        name: `${labelx || "Axial"} (Predicted)`,
        showInLegend: true,
        lineDashType: "dash",
        xValueFormatString: "DD MMM YYYY HH:mm",
        yValueFormatString: "#0.#####",
        dataPoints: this.parseDataPoints(nChartArrX),
        color: "#008FFB",
        lineThickness: 2,
        markerSize: 4,
        markerType: "circle"
      });
    }

    if (nChartArrY?.length) {
      dataSeries.push({
        type: "line",
        name: `${labely || "Tangential"} (Predicted)`,
        showInLegend: true,
        lineDashType: "dash",
        xValueFormatString: "DD MMM YYYY HH:mm",
        yValueFormatString: "#0.#####",
        dataPoints: this.parseDataPoints(nChartArrY),
        color: "#00E396",
        lineThickness: 2,
        markerSize: 4,
        markerType: "circle"
      });
    }

    if (nChartArrZ?.length) {
      dataSeries.push({
        type: "line",
        name: `${labelz || "Radial"} (Predicted)`,
        showInLegend: true,
        lineDashType: "dash",
        xValueFormatString: "DD MMM YYYY HH:mm",
        yValueFormatString: "#0.#####",
        dataPoints: this.parseDataPoints(nChartArrZ),
        color: "#FEB019",
        lineThickness: 2,
        markerSize: 4,
        markerType: "circle"
      });
    }

    return dataSeries;
  }
}
